import { BackofficePermission } from './permissions';

export interface BackofficeRole {
  id: string;
  name: string;
  permissions: BackofficePermission[];
}

export interface BackofficeUser {
  id: string;
  username: string;
  roleIds: string[];
}

export interface PermissionOption {
  value: BackofficePermission;
  label: string;
}

export const BACKOFFICE_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: 'viewing', label: 'Viewing' },
  { value: 'configuration', label: 'Configuration' },
  { value: 'handle_customer_data', label: 'Handle customer data' },
  { value: 'reports', label: 'Reports' },
  {
    value: 'manage_backoffice_users',
    label: 'Manage Backoffice users & roles',
  },
  { value: 'manage_hub_users', label: 'Manage Hub users' },
  { value: 'toggle_feature_flags', label: 'Enable/disable feature flags' },
];

export const ADMIN_ROLE_ID = 'role-admin';
export const MEMBER_ROLE_ID = 'role-member';
export const SUPPORT_ROLE_ID = 'role-support';

export const isBackofficeAdminRole = (role: BackofficeRole) =>
  role.permissions.includes('manage_backoffice_users');

export const hasBackofficeAdmin = (
  users: BackofficeUser[],
  roles: BackofficeRole[],
) => {
  const adminRoleIds = new Set(
    roles.filter(isBackofficeAdminRole).map(role => role.id),
  );
  return users.some(user => user.roleIds.some(id => adminRoleIds.has(id)));
};

export const backofficeUserRoleSelectionBlockedReason = (
  users: BackofficeUser[],
  roles: BackofficeRole[],
  userId: string | null,
  roleIds: string[],
): string | null => {
  if (!userId) return null;
  const nextUsers = users.map(user =>
    user.id === userId ? { ...user, roleIds } : user,
  );
  return hasBackofficeAdmin(nextUsers, roles)
    ? null
    : 'This is the only administrator. Keep an administrator role, or give another user one first.';
};

export const backofficeRolePermissionSelectionBlockedReason = (
  users: BackofficeUser[],
  roles: BackofficeRole[],
  roleId: string | null,
  permissions: BackofficePermission[],
): string | null => {
  if (!roleId) return null;
  const nextRoles = roles.map(role =>
    role.id === roleId ? { ...role, permissions } : role,
  );
  return hasBackofficeAdmin(users, nextRoles)
    ? null
    : 'This is the only role granting administrator access. Keep the administrator permission, or grant it to another role first.';
};

export const INITIAL_BACKOFFICE_ROLES: BackofficeRole[] = [
  {
    id: ADMIN_ROLE_ID,
    name: 'Admin',
    permissions: ['viewing', 'manage_backoffice_users'],
  },
  {
    id: SUPPORT_ROLE_ID,
    name: 'Support',
    permissions: [
      'viewing',
      'configuration',
      'handle_customer_data',
      'manage_hub_users',
      'reports',
      'toggle_feature_flags',
    ],
  },
  {
    id: 'role-developer',
    name: 'Developer',
    permissions: [
      'viewing',
      'configuration',
      'reports',
      'toggle_feature_flags',
    ],
  },
  {
    id: MEMBER_ROLE_ID,
    name: 'Member',
    permissions: ['viewing', 'reports'],
  },
];
